import {IMetadata, IOrganization} from "../IOrganization";
import {Button, Divider, Grid, MenuItem, Select, TextField} from "@leap4/frontend";
import React, {useEffect, useMemo, useState} from "react";
import {setAlert} from "../../../../actions/alert";
import {AlertTypes} from "../../../../utils/alert.utils";
import {PublisherLoginsAccess} from "./PublisherLoginsAccess";
import apiClient from "../../../../actions/apiClient";
import {organizationPartnersUrl} from "../../../../config/generals";
import Q from "bluebird"

export interface IPublisherLoginsProps {
    allOrganizations: Array<IOrganization>,
    updatePublisherLogins: (publisherLogins: IOrganizationPartners[]) => void, // function to update currentOrgPubLogins
    currentOrganization?: IOrganization // created\edited Organization

}

export interface IOrganizationPartners {
    org: IOrganization
    optionalPartners: string[],
    selectedPartners: string[],
    toSkipDiscrepancy: boolean
}

export const PublisherLoginsContainer = (props: IPublisherLoginsProps) => {
    const {allOrganizations, updatePublisherLogins, currentOrganization} = props
    const getInitialOrg = (orgMetadata?: IMetadata): IOrganizationPartners[] => {
        if (!orgMetadata) return [{org: null, optionalPartners: [], selectedPartners: [], toSkipDiscrepancy: false}];
        const organizationPartners: IOrganizationPartners[] = Object.entries(orgMetadata.publisherLogin).map(([orgId, plObj]) => {
            const theOrganization = allOrganizations.find(o => o._id.toString() === orgId)
            return {org: theOrganization, optionalPartners: plObj?.partners, selectedPartners: plObj?.partners, toSkipDiscrepancy: plObj?.toSkipDiscrepancy || false}
        })
        return organizationPartners
    }
    const initialSelectedOrganizations = getInitialOrg(currentOrganization.metadata)
    const [selectedOrganizations, setSelectedOrganizations] = useState<IOrganizationPartners[]>(initialSelectedOrganizations);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const isMoreOrganizations = () => {
        const _isMoreOrganizations = selectedOrganizations.length !== allOrganizations.length;
        if (_isMoreOrganizations) return {boolean: true, text: 'ADD ORGANIZATION'};
        return {boolean: false, text: 'NO MORE ORGANIZATIONS'}
    }
    const onAddOrganization = () => {
        if (selectedOrganizations.length === allOrganizations.length) return;
        const organizationPartners = [...selectedOrganizations, getInitialOrg()[0]]
        setSelectedOrganizations(organizationPartners);

    }
    const updateSelectedOrgs = async (id: string, previousId: string | undefined) => {
        const organizationToSet = allOrganizations.find(o => o._id === id);
        if (!organizationToSet) return setAlert(`Couldn't find Organization By ID:${id}`, AlertTypes.FAILURE)
        const optionalPartnersForOrg = await getPartnersForOrganization(id);
        const organizationsToSet = [...selectedOrganizations]

        if (previousId) {
            const previousIndex = selectedOrganizations.findIndex(o => o.org?._id === previousId)
            organizationsToSet[previousIndex] = {
                org: organizationToSet,
                optionalPartners: optionalPartnersForOrg,
                selectedPartners: organizationsToSet[previousIndex].selectedPartners || [],
                toSkipDiscrepancy: organizationsToSet[previousIndex].toSkipDiscrepancy
            }

        } else {
            const nullIndex = selectedOrganizations.findIndex(o => !o.org?._id)
            organizationsToSet[nullIndex] = {
                org: organizationToSet,
                optionalPartners: [],
                selectedPartners: [],
                toSkipDiscrepancy: false
            }

        }
        setSelectedOrganizations(organizationsToSet)
    }
    const handlePartnersChange = (chosenPartners: string[], forOrganizationId: string) => {
        const copyOfSelectedOrgs = [...selectedOrganizations];
        const organizationToEditIdx = copyOfSelectedOrgs.findIndex(sO => sO.org._id === forOrganizationId);
        copyOfSelectedOrgs[organizationToEditIdx].selectedPartners = chosenPartners;
        setSelectedOrganizations(copyOfSelectedOrgs)
    }
    const handleOrganizationRemove = (orgIdToRemove: string) => {
        const newSelectedOrgs = selectedOrganizations.filter(o => o.org?._id !== orgIdToRemove);
        setSelectedOrganizations(newSelectedOrgs)
    };


    const getPartnersForOrganization = async (orgId: string) => {
        setIsLoading(true)
        try {
            const optionalPartnersFromApi = (await apiClient.get(organizationPartnersUrl, {params: {organization: orgId.toString()}})).data
            return optionalPartnersFromApi;


        } catch (err) {
            setAlert(`Couldn't load Partners, err is: ${err}`, AlertTypes.FAILURE)

        } finally {
            setIsLoading(false)
        }
    }

    const fetchOptionalPartners = async () => {
        const newSelectedOrgs: IOrganizationPartners[] = await Q.map(selectedOrganizations, async (o, idx) => {
            const optionalPartners = await getPartnersForOrganization(o?.org._id)

            return {...o, optionalPartners}
        })
        setSelectedOrganizations(newSelectedOrgs)
    }

    const onSkipDiscChange = (toSkipDiscrepancy: boolean, orgId: string) => {
        const copyOfSelectedOrgs = [...selectedOrganizations];
        const organizationToEditIdx = copyOfSelectedOrgs.findIndex(sO => sO.org._id === orgId);
        copyOfSelectedOrgs[organizationToEditIdx].toSkipDiscrepancy = toSkipDiscrepancy;
        setSelectedOrganizations(copyOfSelectedOrgs)
    }

    useEffect(() => {
        if (selectedOrganizations.some(o => !o?.org?._id)) return;
        if (selectedOrganizations.some(s => !s.optionalPartners?.length || typeof s.optionalPartners[0] === 'string')) {
            fetchOptionalPartners()
        }
        updatePublisherLogins(selectedOrganizations)
    }, [selectedOrganizations]);

    const allSelectedOrgIds = selectedOrganizations.map(o => o.org?._id)

    return (
        <>
            <Grid xs={12} margin={"30px"}>
                <h1>Publisher Logins</h1>
                <Divider/>
            </Grid>
            {
                selectedOrganizations.map((sO, idx) => {
                    const availableOrganizations = allOrganizations.filter(org => allSelectedOrgIds.indexOf(org._id) === -1 && org._id)
                    sO.org?._id && availableOrganizations.unshift(sO.org)
                    return <PublisherLoginsAccess organization={sO}
                                                  isLoading={isLoading}
                                                  availableOrganizations={availableOrganizations}
                                                  onUpdateSelectedOrgs={updateSelectedOrgs}
                                                  onPartnersChange={handlePartnersChange}
                                                  onSkipDiscChange={onSkipDiscChange}
                                                  onOrganizationRemove={handleOrganizationRemove}
                                                  key={idx + Math.random()}

                    />
                })
            }

            <Button size="medium" variant="contained" color="primary"
                    disabled={!isMoreOrganizations().boolean}
                    onClick={onAddOrganization}>
                {isMoreOrganizations().text}
            </Button>


        </>
    )
}



